import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  getReminderTemplates,
  previewReminder,
  saveReminderTemplate,
  sendReminder,
  sendTestReminder,
  workflowCompanies,
} from "./service/WorkflowService";
import useDocumentAccess from "./useDocumentAccess";
import CompanyDocumentsLanguageToggle from "./components/CompanyDocumentsLanguageToggle";
import {
  CompanyDocumentsLanguageProvider,
  useCompanyDocumentsLanguage,
} from "./i18n/CompanyDocumentsLanguageContext";

const fallbackTemplates = [
  {
    template: "A",
    name: "Corporate Credentials belum lengkap",
    subject: "[Qaptive] Reminder Kelengkapan Corporate Credentials",
    body:
      "Yth. Bapak/Ibu,\n\nKami mengingatkan bahwa kelengkapan Company Documents untuk {{company_name}} masih memerlukan perhatian pada:\n\n{{missing_items}}\n\nMohon melakukan pembaruan melalui Qaptive.\n\nTerima kasih,\nPT Sinartama Gunita",
  },
  {
    template: "C",
    name: "Management & Corporate Secretary belum lengkap",
    subject: "[Qaptive] Reminder Kelengkapan Management & Corporate Secretary",
    body:
      "Yth. Bapak/Ibu,\n\nKami mengingatkan bahwa kelengkapan Company Documents untuk {{company_name}} masih memerlukan perhatian pada:\n\n{{missing_items}}\n\nMohon melakukan pembaruan melalui Qaptive.\n\nTerima kasih,\nPT Sinartama Gunita",
  },
  {
    template: "AC",
    name: "Corporate Credentials + Management belum lengkap",
    subject: "[Qaptive] Reminder Kelengkapan Company Documents",
    body:
      "Yth. Bapak/Ibu,\n\nKami mengingatkan bahwa kelengkapan Company Documents untuk {{company_name}} masih memerlukan perhatian pada:\n\n{{missing_items}}\n\nMohon melakukan pembaruan melalui Qaptive.\n\nTerima kasih,\nPT Sinartama Gunita",
  },
];

const testEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const renderDraft = (value, companyName, missingItems) =>
  (value || "")
    .replaceAll("{{company_name}}", companyName)
    .replaceAll("{{missing_items}}", missingItems);

function ReminderSenderPageContent({ setLayoutAction }) {
  const access = useDocumentAccess(true, "reminder-sender");
  const { t } =
    useCompanyDocumentsLanguage();

  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState([]);
  const [all, setAll] = useState(false);
  const [template, setTemplate] = useState("A");
  const [templates, setTemplates] = useState(fallbackTemplates);
  const [subject, setSubject] = useState(fallbackTemplates[0].subject);
  const [body, setBody] = useState(fallbackTemplates[0].body);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [testRecipient, setTestRecipient] = useState("");

  useEffect(() => {
    setLayoutAction(null);

    workflowCompanies()
      .then((rows) =>
        setCompanies(
          [...rows].sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", "id", {
              sensitivity: "base",
            }),
          ),
        ),
      )
      .catch((err) => setError(err.message));

    getReminderTemplates()
      .then((rows) => {
        if (!rows?.length) return;
        setTemplates(rows);
        const current = rows.find((row) => row.template === "A") || rows[0];
        setTemplate(current.template);
        setSubject(current.subject);
        setBody(current.body);
      })
      .catch((err) => setError(err.message));
  }, [setLayoutAction]);


  const currentTemplate = useMemo(
    () => templates.find((row) => row.template === template),
    [template, templates],
  );

  const testMode = testRecipient.trim() !== "";

  const liveCompany = all
    ? t("reminder.liveAll")
    : selected[0]?.name ||
      t("reminder.liveIssuer");

  const liveMissing =
    t("reminder.liveMissing");
  const liveSubject = renderDraft(subject, liveCompany, liveMissing);
  const liveBody = renderDraft(body, liveCompany, liveMissing);

  const displayedSubject = testMode ? subject : liveSubject;
  const displayedBody = testMode ? body : liveBody;

  const input = {
    template,
    all_active: all,
    company_ids: selected.map((company) => company.id),
    owner_ids: [],
    subject,
    body,
  };

  const canUseAll = ["A", "C", "AC"].includes(template);
  const hasIssuerSelection = all || selected.length > 0;
  const canPreview =
    !testMode &&
    hasIssuerSelection &&
    subject.trim() &&
    body.trim();

  async function execute(fn) {
    setBusy(true);
    setError("");
    setResult("");
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const changeTemplate = (code) => {
    const next =
      templates.find((row) => row.template === code) ||
      fallbackTemplates.find((row) => row.template === code);

    setTemplate(code);
    setAll(false);
    setSelected([]);
    setPreview(null);
    setRequestId("");

    if (next) {
      setSubject(next.subject);
      setBody(next.body);
    }
  };

  if (!access.review) {
    return (
      <Alert severity="info">
        {t("reminder.permission")}
      </Alert>
    );
  }

  return (
    <section className="grid gap-5 min-w-0">
      <div className="flex justify-end">
        <CompanyDocumentsLanguageToggle />
      </div>

      <div className="min-w-0">
        <h1 className="text-xl font-bold">
          {t("reminder.title")}
        </h1>
        <p className="mt-1 text-sm text-[#5F737F]">
          {t("reminder.description")}
        </p>
      </div>

      {error && <Alert severity="error">{error}</Alert>}
      {result && <Alert severity="success">{result}</Alert>}

      <div className="rounded-lg border bg-white p-4 md:p-5 grid gap-4 min-w-0">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <TextField
            select
            fullWidth
            label={t("reminder.template")}
            value={template}
            onChange={(event) => changeTemplate(event.target.value)}
          >
            {templates
              .filter((row) => ["A", "C", "AC"].includes(row.template))
              .map((row) => (
                <MenuItem key={row.template} value={row.template}>
                  {row.name}
                </MenuItem>
              ))}
          </TextField>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outlined"
              disabled={!canUseAll || busy || testMode}
              onClick={() => {
                setAll(true);
                setSelected([]);
                setPreview(null);
                setRequestId("");
              }}
            >
              {t("reminder.selectAll")}
            </Button>
            <Button
              disabled={busy || (!all && !selected.length)}
              onClick={() => {
                setAll(false);
                setSelected([]);
                setPreview(null);
                setRequestId("");
              }}
            >
              {t("reminder.clear")}
            </Button>
          </div>
        </div>

        {all ? (
          <div className="flex items-center gap-2 flex-wrap">
            <Chip
              color="primary"
              variant="outlined"
              label={t("reminder.allSelected")}
            />
            <span className="text-sm text-[#5F737F]">
              {t("reminder.available", {
                count: companies.length,
              })}
            </span>
          </div>
        ) : (
          <Autocomplete
            multiple
            fullWidth
            limitTags={3}
            disabled={busy || testMode}
            options={companies}
            value={selected}
            getOptionLabel={(company) =>
              `${company.name} (${company.effect_code})`
            }
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, value) => {
              setSelected(value);
              setPreview(null);
              setRequestId("");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(
                  "reminder.selectedIssuers",
                )}
              />
            )}
          />
        )}

      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 min-w-0">
        <div className="rounded-lg border bg-white p-4 md:p-5 grid gap-4 min-w-0">
          <div>
            <h2 className="font-semibold">
              {t("reminder.editor.title")}
            </h2>
            <p className="mt-1 text-xs text-[#5F737F]">
              {t("reminder.editor.placeholder")}
            </p>
          </div>

                    <div className="grid gap-2 min-w-0">
            <label
              htmlFor="reminder-email-subject"
              className="text-sm font-medium text-[#253238]"
            >
              {t("reminder.editor.subject")}
            </label>
            <input
              id="reminder-email-subject"
              type="text"
              className="w-full min-w-0 rounded-md border border-[#C4CDD5] bg-white px-3 py-2.5 text-sm leading-5 outline-none transition focus:border-[#5B74B8] focus:ring-1 focus:ring-[#5B74B8] disabled:bg-[#F8FAFC]"
              value={subject}
              maxLength={500}
              disabled={busy}
              onChange={(event) => {
                setSubject(event.target.value);
                setPreview(null);
                setRequestId("");
              }}
            />
          </div>

                    <div className="grid gap-2 min-w-0">
            <label
              htmlFor="reminder-email-message"
              className="text-sm font-medium text-[#253238]"
            >
              {t("reminder.editor.message")}
            </label>
            <textarea
              id="reminder-email-message"
              rows={10}
              className="min-h-[240px] w-full min-w-0 resize-y rounded-md border border-[#C4CDD5] bg-white px-3 py-3 text-sm leading-6 outline-none transition focus:border-[#5B74B8] focus:ring-1 focus:ring-[#5B74B8] disabled:bg-[#F8FAFC]"
              value={body}
              maxLength={5000}
              disabled={busy}
              onChange={(event) => {
                setBody(event.target.value);
                setPreview(null);
                setRequestId("");
              }}
            />
          </div>

          <div className="grid gap-2 min-w-0">
            <label
              htmlFor="reminder-test-recipient"
              className="text-sm font-medium text-[#253238]"
            >
              {t(
                "reminder.editor.testRecipient",
              )}
            </label>
            <input
              id="reminder-test-recipient"
              type="email"
              autoComplete="off"
              placeholder="nama@contoh.com"
              className="w-full min-w-0 rounded-md border border-[#C4CDD5] bg-white px-3 py-2.5 text-sm leading-5 outline-none transition focus:border-[#5B74B8] focus:ring-1 focus:ring-[#5B74B8] disabled:bg-[#F8FAFC]"
              value={testRecipient}
              maxLength={320}
              disabled={busy}
              onChange={(event) => {
                const value = event.target.value;

                setTestRecipient(value);

                if (value.trim()) {
                  setAll(false);
                  setSelected([]);
                  setPreview(null);
                  setRequestId("");
                }
              }}
            />
            <p className="text-xs text-[#5F737F]">
              {t("reminder.editor.testHelp")}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outlined"
              disabled={busy || !subject.trim() || !body.trim()}
              onClick={() =>
                execute(async () => {
                  const saved = await saveReminderTemplate(template, {
                    subject,
                    body,
                  });
                  setTemplates((rows) =>
                    rows.map((row) =>
                      row.template === saved.template ? saved : row,
                    ),
                  );
                  setResult(
                    t(
                      "reminder.result.templateSaved",
                      { name: saved.name },
                    ),
                  );
                })
              }
            >
              {t(
                "reminder.actions.saveTemplate",
              )}
            </Button>

            <Button
              variant="outlined"
              disabled={
                busy ||
                !subject.trim() ||
                !body.trim() ||
                !testEmailPattern.test(testRecipient.trim())
              }
              onClick={() =>
                execute(async () => {
                  const recipient = testRecipient.trim();

                  await sendTestReminder({
                    test_recipient: recipient,
                    subject,
                    body,
                  });

                  setResult(
                    t(
                      "reminder.result.testSent",
                      { recipient },
                    ),
                  );
                })
              }
            >
              {t(
                "reminder.actions.sendTest",
              )}
            </Button>

            <Button
              variant="contained"
              disabled={busy || !canPreview}
              onClick={() =>
                execute(async () => {
                  setPreview(await previewReminder(input));
                  setRequestId(crypto.randomUUID());
                })
              }
            >
              {t(
                "reminder.actions.preview",
              )}
            </Button>
          </div>

          {currentTemplate && (
            <p className="text-xs text-[#5F737F]">
              {t(
                "reminder.editing",
                {
                  name:
                    currentTemplate.name,
                },
              )}
            </p>
          )}
        </div>

        <div className="rounded-lg border bg-white p-4 md:p-5 min-w-0">
          <h2 className="font-semibold">
            {t("reminder.livePreview")}
          </h2>
          <div className="mt-4 rounded-lg border overflow-hidden min-w-0">
            <div className="border-b bg-[#F8FAFC] p-3">
              <p className="text-xs text-[#5F737F]">
                {t(
                  "reminder.previewSubject",
                )}
              </p>
              <p className="font-semibold break-words">
                {displayedSubject || "—"}
              </p>
            </div>
            <div className="p-4">
              <pre className="whitespace-pre-wrap break-words font-sans text-sm">
                {displayedBody || "—"}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {preview && (
        <div className="rounded-lg border bg-white p-4 md:p-5 grid gap-4 min-w-0">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-semibold">
                {t(
                  "reminder.validatedPreview",
                )}
              </h2>
              <p className="text-sm text-[#5F737F]">
                {t(
                  "reminder.recipientSummary",
                  {
                    companies:
                      preview.company_count,
                    recipients:
                      preview.recipient_count,
                  },
                )}
              </p>
            </div>
            <Chip
              variant="outlined"
              color="success"
              label={t(
                "reminder.recipientValidated",
              )}
            />
          </div>

          <div className="grid gap-3">
            {preview.messages.map((message) => (
              <article
                key={message.company_id}
                className="rounded-lg border overflow-hidden min-w-0"
              >
                <div className="border-b bg-[#F8FAFC] p-3">
                  <p className="text-xs text-[#5F737F]">
                    {message.company_name}
                  </p>
                  <strong className="break-words">{message.subject}</strong>
                </div>
                <pre className="whitespace-pre-wrap break-words font-sans text-sm p-4">
                  {message.body}
                </pre>
              </article>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="contained"
              disabled={busy || !preview.recipient_count}
              onClick={() =>
                execute(async () => {
                  await sendReminder({
                    ...input,
                    request_id: requestId,
                    fingerprint: preview.fingerprint,
                  });
                  setPreview(null);
                  setResult(
                    t(
                      "reminder.result.queued",
                    ),
                  );
                })
              }
            >
              {t(
                "reminder.actions.send",
              )}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

export default function ReminderSenderPage(props) {
  return (
    <CompanyDocumentsLanguageProvider>
      <ReminderSenderPageContent {...props} />
    </CompanyDocumentsLanguageProvider>
  );
}
